import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Save, MapPin, Plus, Edit, Trash2, Wrench, Settings, Check } from 'lucide-react';
import { DealerLocation, ServiceBay, CreateServiceBayRequest, ServiceType, DealerService, UpdateBayServicesRequest } from '../types';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  
  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const LocationInfo = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  
  .location-name {
    font-weight: 600;
    color: #0c4a6e;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .location-details {
    font-size: 0.875rem;
    color: #0369a1;
  }
`;

const BaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
`;

const BayCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.2s;
  
  &:hover {
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.1);
  }
  
  .bay-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    
    .bay-info {
      .bay-title {
        font-weight: 600;
        color: #1f2937;
        font-size: 1.125rem;
        margin-bottom: 4px;
      }
      
      .bay-description {
        font-size: 0.875rem;
        color: #6b7280;
      }
    }
    
    .bay-actions {
      display: flex;
      gap: 8px;
    }
  }
  
  .bay-services {
    .services-title {
      font-weight: 600;
      color: #374151;
      font-size: 0.875rem;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    
    .services-list {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      
      .service-tag {
        background: #eff6ff;
        color: #1e40af;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 0.75rem;
        font-weight: 500;
        border: 1px solid #bfdbfe;
      }
      
      .no-services {
        color: #9ca3af;
        font-style: italic;
        font-size: 0.875rem;
      }
    }
  }
`;

const AddBayCard = styled.div`
  background: white;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #6b7280;
  
  &:hover {
    border-color: #ed2024;
    color: #ed2024;
    background: #f8f9ff;
  }
  
  .add-icon {
    margin-bottom: 12px;
  }
  
  .add-text {
    font-weight: 600;
  }
`;

const ServiceAssignmentModal = styled.div<{ isOpen: boolean }>`
  display: ${(props: { isOpen: boolean }) => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
`;

const ServiceModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  
  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    
    h3 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    button {
      background: none;
      border: none;
      font-size: 1.5rem;
      color: #6b7280;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:hover {
        color: #374151;
      }
    }
  }
`;

const ServiceSelectionControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
  
  .selection-info {
    font-size: 0.875rem;
    color: #6b7280;
    
    .count {
      font-weight: 600;
      color: #374151;
    }
  }
  
  .bulk-actions {
    display: flex;
    gap: 8px;
    
    button {
      padding: 4px 8px;
      border: 1px solid #d1d5db;
      background: white;
      color: #374151;
      border-radius: 4px;
      font-size: 0.75rem;
      cursor: pointer;
      transition: all 0.2s;
      
      &:hover {
        background: #f3f4f6;
        border-color: #9ca3af;
      }
    }
  }
`;

const ServicesList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  margin: 16px 0;
`;

const ServiceItem = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 1px solid ${(props: { isSelected: boolean }) => props.isSelected ? '#3b82f6' : '#e5e7eb'};
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(props: { isSelected: boolean }) => props.isSelected ? '#f0f9ff' : 'white'};
  
  &:hover {
    border-color: ${(props: { isSelected: boolean }) => props.isSelected ? '#2563eb' : '#9ca3af'};
    background: ${(props: { isSelected: boolean }) => props.isSelected ? '#eff6ff' : '#f9fafb'};
  }
  
  .checkbox {
    width: 18px;
    height: 18px;
    border: 2px solid ${(props: { isSelected: boolean }) => props.isSelected ? '#3b82f6' : '#d1d5db'};
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(props: { isSelected: boolean }) => props.isSelected ? '#3b82f6' : 'white'};
    flex-shrink: 0;
    margin-top: 1px;
    
    svg {
      color: white;
      width: 12px;
      height: 12px;
    }
  }
  
  .service-info {
    flex: 1;
    
    .service-name {
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 4px;
    }
    
    .service-description {
      font-size: 0.875rem;
      color: #6b7280;
      line-height: 1.4;
    }
    
    .service-meta {
      display: flex;
      gap: 16px;
      margin-top: 8px;
      font-size: 0.75rem;
      color: #9ca3af;
      
      .duration {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      
      .price {
        display: flex;
        align-items: center;
        gap: 4px;
      }
    }
  }
`;

const ServiceModalActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
  
  .changes-info {
    font-size: 0.875rem;
    color: #6b7280;
    
    .count {
      font-weight: 600;
      color: #374151;
    }
  }
  
  .action-buttons {
    display: flex;
    gap: 12px;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: ${(props: { variant?: 'primary' | 'secondary' | 'danger' }) => 
    props.variant === 'primary' ? 'none' : 
    props.variant === 'danger' ? '1px solid #ef4444' : '1px solid #d1d5db'
  };
  background: ${(props: { variant?: 'primary' | 'secondary' | 'danger' }) => 
    props.variant === 'primary' ? 'linear-gradient(135deg, #ed2024 0%, #a01519 100%)' : 
    props.variant === 'danger' ? '#fef2f2' : 'white'
  };
  color: ${(props: { variant?: 'primary' | 'secondary' | 'danger' }) => 
    props.variant === 'primary' ? 'white' : 
    props.variant === 'danger' ? '#dc2626' : '#374151'
  };
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  
  &:hover:not(:disabled) {
    ${(props: { variant?: 'primary' | 'secondary' | 'danger' }) => 
      props.variant === 'primary' ? `
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
      ` : props.variant === 'danger' ? `
        background: #fee2e2;
        border-color: #fca5a5;
      ` : `
        background: #f3f4f6;
        border-color: #9ca3af;
      `
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${(props: { isOpen: boolean }) => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  
  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    
    h3 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
    }
    
    button {
      background: none;
      border: none;
      font-size: 1.5rem;
      color: #6b7280;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:hover {
        color: #374151;
      }
    }
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid ${(props: { hasError?: boolean }) => props.hasError ? '#ef4444' : '#e5e7eb'};
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${(props: { hasError?: boolean }) => props.hasError ? '#ef4444' : '#ed2024'};
    box-shadow: 0 0 0 3px ${(props: { hasError?: boolean }) => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(102, 126, 234, 0.1)'};
  }
`;

const TextArea = styled.textarea<{ hasError?: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid ${(props: { hasError?: boolean }) => props.hasError ? '#ef4444' : '#e5e7eb'};
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s;
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: ${(props: { hasError?: boolean }) => props.hasError ? '#ef4444' : '#ed2024'};
    box-shadow: 0 0 0 3px ${(props: { hasError?: boolean }) => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(102, 126, 234, 0.1)'};
  }
`;

const ErrorMessage = styled.span`
  color: #ef4444;
  font-size: 0.875rem;
`;

const HelpText = styled.span`
  color: #6b7280;
  font-size: 0.75rem;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #ed2024;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LocationBaysPage: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [location, setLocation] = useState<DealerLocation | null>(null);
  const [bays, setBays] = useState<ServiceBay[]>([]);
  const [dealerServices, setDealerServices] = useState<DealerService[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [editingBay, setEditingBay] = useState<ServiceBay | null>(null);
  const [assigningServicesBay, setAssigningServicesBay] = useState<ServiceBay | null>(null);
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set());
  const [isSavingServices, setIsSavingServices] = useState(false);
  const [formData, setFormData] = useState({
    bayNumber: '',
    bayName: '',
    description: ''
  });

  useEffect(() => {
    if (locationId) {
      loadLocationAndBays();
    }
  }, [locationId]);

  const loadLocationAndBays = async () => {
    if (!locationId || !user) return;

    try {
      setIsLoading(true);
      
      let foundLocation: DealerLocation | undefined;
      
      if (user.userType === 'dealer') {
        // For dealer users, get their dealer's locations
        const response = await apiService.getDealerLocations(user.dealerId);
        if (response.success && response.data) {
          foundLocation = response.data.find(loc => loc.id === locationId);
          
          // Check if staff user has access to this location
          if (foundLocation && (user as any).role === 'dealer_staff') {
            const userLocationIds = (user as any).locationIds || [];
            if (!userLocationIds.includes(locationId)) {
              toast.error('You do not have access to this location');
              navigate('/locations');
              return;
            }
          }
        }
      } else {
        // For admin users, try sessionStorage first, then search all dealers
        const dealerId = sessionStorage.getItem('currentDealerId');
        if (dealerId) {
          const response = await apiService.getDealerLocations(dealerId);
          if (response.success && response.data) {
            foundLocation = response.data.find(loc => loc.id === locationId);
          }
        }
        
        if (!foundLocation) {
          const dealersResponse = await apiService.getDealers();
          if (dealersResponse.success && dealersResponse.data) {
            for (const dealer of dealersResponse.data) {
              const locationsResponse = await apiService.getDealerLocations(dealer.id);
              if (locationsResponse.success && locationsResponse.data) {
                const location = locationsResponse.data.find(loc => loc.id === locationId);
                if (location) {
                  foundLocation = location;
                  sessionStorage.setItem('currentDealerId', dealer.id);
                  break;
                }
              }
            }
          }
        }
      }

      if (!foundLocation) {
        toast.error('Location not found');
        navigate('/dealers');
        return;
      }

      setLocation(foundLocation);

      // Load service bays
      const baysResponse = await apiService.getLocationBays(locationId);
      if (baysResponse.success && baysResponse.data) {
        setBays(baysResponse.data);
      }

      // Load dealer services for service assignment
      const dealerId = foundLocation.dealerId;
      const servicesResponse = await apiService.getDealerServices(dealerId);
      if (servicesResponse.success && servicesResponse.data) {
        setDealerServices(servicesResponse.data);
      }
    } catch (error) {
      console.error('Error loading location data:', error);
      toast.error('Failed to load location data');
      navigate('/dealers');
    } finally {
      setIsLoading(false);
    }
  };

  const getNextBayNumber = (): number => {
    if (bays.length === 0) return 1;
    const maxBayNumber = Math.max(...bays.map(bay => bay.bayNumber));
    return maxBayNumber + 1;
  };

  const handleAddBay = async () => {
    if (!formData.bayName.trim() || !formData.bayNumber.trim() || !locationId) return;

    try {
      const bayData: CreateServiceBayRequest = {
        locationId,
        bayNumber: parseInt(formData.bayNumber),
        bayName: formData.bayName.trim(),
        description: formData.description.trim() || undefined
      };

      const response = await apiService.createServiceBay(locationId, bayData);
      if (response.success && response.data) {
        setBays(prev => [...prev, response.data!].sort((a, b) => a.bayNumber - b.bayNumber));
        setFormData({ bayNumber: '', bayName: '', description: '' });
        setIsModalOpen(false);
        toast.success('Service bay added successfully');
      } else {
        toast.error('Failed to add service bay');
      }
    } catch (error) {
      console.error('Error adding service bay:', error);
      toast.error('Failed to add service bay');
    }
  };

  const handleEditBay = (bay: ServiceBay) => {
    setEditingBay(bay);
    setFormData({
      bayNumber: bay.bayNumber.toString(),
      bayName: bay.bayName,
      description: bay.description || ''
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateBay = async () => {
    if (!formData.bayName.trim() || !formData.bayNumber.trim() || !editingBay) return;

    try {
      const bayData = {
        id: editingBay.id,
        bayNumber: parseInt(formData.bayNumber),
        bayName: formData.bayName.trim(),
        description: formData.description.trim() || undefined
      };

      const response = await apiService.updateServiceBay(editingBay.id, bayData);
      if (response.success && response.data) {
        setBays(prev => prev.map(bay => 
          bay.id === editingBay.id ? { ...bay, ...response.data! } : bay
        ).sort((a, b) => a.bayNumber - b.bayNumber));
        setIsEditModalOpen(false);
        setEditingBay(null);
        setFormData({ bayNumber: '', bayName: '', description: '' });
        toast.success('Service bay updated successfully');
      } else {
        toast.error('Failed to update service bay');
      }
    } catch (error) {
      console.error('Error updating service bay:', error);
      toast.error('Failed to update service bay');
    }
  };

  const handleDeleteBay = async (bay: ServiceBay) => {
    if (!window.confirm(`Are you sure you want to delete "${bay.bayName}"? This will also remove all service assignments for this bay.`)) {
      return;
    }

    try {
      const response = await apiService.deleteServiceBay(bay.id);
      if (response.success) {
        setBays(prev => prev.filter(b => b.id !== bay.id));
        toast.success('Service bay deleted successfully');
      } else {
        toast.error('Failed to delete service bay');
      }
    } catch (error) {
      console.error('Error deleting service bay:', error);
      toast.error('Failed to delete service bay');
    }
  };

  const updateFormField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openAddModal = () => {
    setFormData({ 
      bayNumber: getNextBayNumber().toString(), 
      bayName: '', 
      description: '' 
    });
    setIsModalOpen(true);
  };

  const handleBack = () => {
    if (user?.userType === 'dealer') {
      navigate('/locations');
    } else if (location) {
      const dealerId = sessionStorage.getItem('currentDealerId') || location.dealerId;
      navigate(`/dealers/${dealerId}/edit`);
    } else {
      navigate('/dealers');
    }
  };

  // Service Assignment Functions
  const openServiceAssignmentModal = (bay: ServiceBay) => {
    setAssigningServicesBay(bay);
    
    // Set currently assigned services as selected
    const currentServiceIds = new Set(
      bay.services?.map(service => service.serviceId) || []
    );
    setSelectedServices(currentServiceIds);
    setIsServiceModalOpen(true);
  };

  const toggleServiceSelection = (serviceId: string) => {
    const newSelected = new Set(selectedServices);
    if (newSelected.has(serviceId)) {
      newSelected.delete(serviceId);
    } else {
      newSelected.add(serviceId);
    }
    setSelectedServices(newSelected);
  };

  const selectAllServices = () => {
    const allServiceIds = new Set(dealerServices.map(service => service.id));
    setSelectedServices(allServiceIds);
  };

  const unselectAllServices = () => {
    setSelectedServices(new Set());
  };

  const handleSaveServiceAssignments = async () => {
    if (!assigningServicesBay) return;

    try {
      setIsSavingServices(true);
      
      // Prepare services data for the API
      const servicesData: UpdateBayServicesRequest = {
        bayId: assigningServicesBay.id,
        services: Array.from(selectedServices).map(serviceId => {
          const service = dealerServices.find(s => s.id === serviceId);
          return {
            serviceId: serviceId,
            serviceName: service?.name || 'Unknown Service'
          };
        })
      };

      const response = await apiService.updateBayServices(assigningServicesBay.id, servicesData);
      if (response.success) {
        // Update the bay in the local state
        setBays(prev => prev.map(bay => {
          if (bay.id === assigningServicesBay.id) {
            return {
              ...bay,
              services: servicesData.services.map((service, index) => ({
                id: `${bay.id}-${service.serviceId}`,
                bayId: bay.id,
                serviceId: service.serviceId,
                serviceName: service.serviceName,
                isActive: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              }))
            };
          }
          return bay;
        }));

        toast.success('Service assignments updated successfully');
        setIsServiceModalOpen(false);
        setAssigningServicesBay(null);
        setSelectedServices(new Set());
      } else {
        toast.error('Failed to update service assignments');
      }
    } catch (error) {
      console.error('Error updating service assignments:', error);
      toast.error('Failed to update service assignments');
    } finally {
      setIsSavingServices(false);
    }
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours}h`;
    return `${hours}h ${remainingMinutes}m`;
  };

  const formatPrice = (price?: number): string => {
    if (price === undefined) return '';
    return `$${price.toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </PageContainer>
    );
  }

  if (!location) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
          Location not found
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={handleBack}>
          <ArrowLeft />
          Back
        </BackButton>
        <h1>
          <Wrench />
          Service Bays
        </h1>
      </PageHeader>

      <LocationInfo>
        <div className="location-name">
          <MapPin size={16} />
          {location.name}
        </div>
        <div className="location-details">
          Manage service bays and their assigned services
        </div>
      </LocationInfo>

      <BaysGrid>
        {bays.map((bay) => (
          <BayCard key={bay.id}>
            <div className="bay-header">
              <div className="bay-info">
                <div className="bay-title">Bay {bay.bayNumber}: {bay.bayName}</div>
                {bay.description && (
                  <div className="bay-description">{bay.description}</div>
                )}
              </div>
              <div className="bay-actions">
                <Button onClick={() => handleEditBay(bay)}>
                  <Edit />
                </Button>
                <Button 
                  variant="danger" 
                  onClick={() => handleDeleteBay(bay)}
                >
                  <Trash2 />
                </Button>
              </div>
            </div>
            
            <div className="bay-services">
              <div className="services-title">
                <Settings size={14} />
                Assigned Services
              </div>
              <div className="services-list">
                {bay.services && bay.services.length > 0 ? (
                  bay.services.map(service => (
                    <span key={service.id} className="service-tag">
                      {service.serviceName}
                    </span>
                  ))
                ) : (
                  <span className="no-services">No services assigned</span>
                )}
              </div>
              <div style={{ marginTop: '12px', textAlign: 'right' }}>
                <Button 
                  type="button" 
                  variant="secondary" 
                  onClick={() => openServiceAssignmentModal(bay)}
                >
                  <Wrench size={14} /> 
                  Assign Services
                </Button>
              </div>
            </div>
          </BayCard>
        ))}
        
        <AddBayCard onClick={openAddModal}>
          <div className="add-icon">
            <Plus size={32} />
          </div>
          <div className="add-text">Add Service Bay</div>
        </AddBayCard>
      </BaysGrid>

      {/* Add Bay Modal */}
      <Modal isOpen={isModalOpen}>
        <ModalContent>
          <div className="modal-header">
            <h3>Add Service Bay</h3>
            <button onClick={() => setIsModalOpen(false)}>×</button>
          </div>
          
          <FormRow>
            <FormGroup>
              <Label htmlFor="bayNumber">Bay Number *</Label>
              <Input
                id="bayNumber"
                type="number"
                min="1"
                value={formData.bayNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormField('bayNumber', e.target.value)}
                placeholder="1"
                hasError={false}
              />
              <HelpText>Unique number for this bay</HelpText>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="bayName">Bay Name *</Label>
              <Input
                id="bayName"
                value={formData.bayName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormField('bayName', e.target.value)}
                placeholder="e.g., General Service, Alignment Bay"
                hasError={false}
              />
              <HelpText>Descriptive name for this bay</HelpText>
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label htmlFor="description">Description</Label>
            <TextArea
              id="description"
              value={formData.description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateFormField('description', e.target.value)}
              placeholder="Optional description of this bay's purpose or capabilities"
              hasError={false}
            />
          </FormGroup>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <Button onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleAddBay}
              disabled={!formData.bayName.trim() || !formData.bayNumber.trim()}
            >
              Add Bay
            </Button>
          </div>
        </ModalContent>
      </Modal>

      {/* Edit Bay Modal */}
      <Modal isOpen={isEditModalOpen}>
        <ModalContent>
          <div className="modal-header">
            <h3>Edit Service Bay</h3>
            <button onClick={() => setIsEditModalOpen(false)}>×</button>
          </div>
          
          <FormRow>
            <FormGroup>
              <Label htmlFor="editBayNumber">Bay Number *</Label>
              <Input
                id="editBayNumber"
                type="number"
                min="1"
                value={formData.bayNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormField('bayNumber', e.target.value)}
                placeholder="1"
                hasError={false}
              />
              <HelpText>Unique number for this bay</HelpText>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="editBayName">Bay Name *</Label>
              <Input
                id="editBayName"
                value={formData.bayName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormField('bayName', e.target.value)}
                placeholder="e.g., General Service, Alignment Bay"
                hasError={false}
              />
              <HelpText>Descriptive name for this bay</HelpText>
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label htmlFor="editDescription">Description</Label>
            <TextArea
              id="editDescription"
              value={formData.description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateFormField('description', e.target.value)}
              placeholder="Optional description of this bay's purpose or capabilities"
              hasError={false}
            />
          </FormGroup>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <Button onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleUpdateBay}
              disabled={!formData.bayName.trim() || !formData.bayNumber.trim()}
            >
              Update Bay
            </Button>
          </div>
        </ModalContent>
      </Modal>

      {/* Service Assignment Modal */}
      <ServiceAssignmentModal isOpen={isServiceModalOpen}>
        <ServiceModalContent>
          <div className="modal-header">
            <h3>
              <Wrench />
              Assign Services to {assigningServicesBay?.bayName}
            </h3>
            <button onClick={() => setIsServiceModalOpen(false)}>×</button>
          </div>

          <ServiceSelectionControls>
            <div className="selection-info">
              <span className="count">{selectedServices.size}</span> of {dealerServices.length} services selected
            </div>
            <div className="bulk-actions">
              <button onClick={selectAllServices}>Select All</button>
              <button onClick={unselectAllServices}>Unselect All</button>
            </div>
          </ServiceSelectionControls>

          {dealerServices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <p>No services available. Create services first to assign them to bays.</p>
            </div>
          ) : (
            <ServicesList>
              {dealerServices.map((service) => {
                const isSelected = selectedServices.has(service.id);
                
                return (
                  <ServiceItem
                    key={service.id}
                    isSelected={isSelected}
                    onClick={() => toggleServiceSelection(service.id)}
                  >
                    <div className="checkbox">
                      {isSelected && <Check />}
                    </div>
                    <div className="service-info">
                      <div className="service-name">{service.name}</div>
                      {service.description && (
                        <div className="service-description">{service.description}</div>
                      )}
                      <div className="service-meta">
                        <div className="duration">
                          <Settings size={10} />
                          {formatDuration(service.durationMinutes)}
                        </div>
                        {service.price && (
                          <div className="price">
                            <span>$</span>
                            {formatPrice(service.price)}
                          </div>
                        )}
                      </div>
                    </div>
                  </ServiceItem>
                );
              })}
            </ServicesList>
          )}

          <ServiceModalActions>
            <div className="changes-info">
              {assigningServicesBay && (
                <>
                  Current: <span className="count">{assigningServicesBay.services?.length || 0}</span> services assigned
                </>
              )}
            </div>
            <div className="action-buttons">
              <Button onClick={() => setIsServiceModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleSaveServiceAssignments}
                disabled={isSavingServices}
              >
                {isSavingServices ? 'Saving...' : 'Save Assignments'}
              </Button>
            </div>
          </ServiceModalActions>
        </ServiceModalContent>
      </ServiceAssignmentModal>
    </PageContainer>
  );
};

export default LocationBaysPage;
